'use client';

import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import BottomNav from '@/components/BottomNav';

export default function SignInPage() {
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [error, setError] = useState('');
  const [isIOS, setIsIOS] = useState(false);
  const { user, loading } = useAuth();
  const router = useRouter();

  // Redirect to home if user is already signed in
  useEffect(() => {
    if (!loading && user) {
      router.replace('/');
    }
  }, [user, loading, router]);

  // Detect iOS platform using Capacitor (runs client-side only)
  useEffect(() => {
    const detectPlatform = async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        setIsIOS(Capacitor.getPlatform() === 'ios');
      } catch {
        setIsIOS(false);
      }
    };
    detectPlatform();
  }, []);

  // Listen for deep link callbacks on iOS (Capacitor App plugin)
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const setupAppUrlListener = async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.getPlatform() !== 'ios') return;

        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appUrlOpen', async (event: { url: string }) => {
          const url = event.url;
          if (!url.startsWith('com.imaanmate.app://auth/callback')) return;

          try {
            const urlObj = new URL(url);
            const code = urlObj.searchParams.get('code');
            if (code) {
              const supabase = createClient();
              const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
              if (!exchangeError) {
                const { Browser } = await import('@capacitor/browser');
                await Browser.close();
                window.location.href = '/';
              }
            }
          } catch {
            // silently ignore malformed URLs
          }
        });

        cleanup = () => handle.remove();
      } catch {
        // Capacitor or App plugin not available (web environment)
      }
    };

    setupAppUrlListener();
    return () => cleanup?.();
  }, [router]);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
      const isAndroid = typeof window !== 'undefined' && window.isAndroidApp === true;

      if (isAndroid) {
        // Let MainActivity.java intercept the OAuth URL and open it in a Custom Tab
        const { error: oauthError } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: 'com.imaanmate.app://auth/callback',
          },
        });
        if (oauthError) throw oauthError;
        return;
      }

      if (isIOS) {
        // iOS: use skipBrowserRedirect to get the URL, then open SFSafariViewController via Browser.open()
        const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: 'com.imaanmate.app://auth/callback',
            skipBrowserRedirect: true,
          },
        });
        if (oauthError) throw oauthError;
        if (data?.url) {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url: data.url });
        }
        return;
      }

      // Standard web OAuth flow
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${siteUrl}/auth/callback`,
        },
      });
      if (oauthError) throw oauthError;
    } catch (err: any) {
      setError(err?.message || 'Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setAppleLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
      const isAndroid = typeof window !== 'undefined' && window.isAndroidApp === true;

      if (isAndroid) {
        const { error: oauthError } = await supabase.auth.signInWithOAuth({
          provider: 'apple',
          options: {
            redirectTo: 'com.imaanmate.app://auth/callback',
          },
        });
        if (oauthError) throw oauthError;
        return;
      }

      if (isIOS) {
        // iOS: use skipBrowserRedirect to get the URL, then open SFSafariViewController via Browser.open()
        const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
          provider: 'apple',
          options: {
            redirectTo: 'com.imaanmate.app://auth/callback',
            skipBrowserRedirect: true,
          },
        });
        if (oauthError) throw oauthError;
        if (data?.url) {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url: data.url });
        }
        return;
      }

      // Standard web OAuth flow
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${siteUrl}/auth/callback`,
        },
      });
      if (oauthError) throw oauthError;
    } catch (err: any) {
      setError(err?.message || 'Apple sign-in failed. Please try again.');
      setAppleLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen geometric-bg flex flex-col items-center justify-center px-4 py-8 pb-24">
        <div className="w-8 h-8 border-2 border-accent/40 border-t-accent rounded-full animate-spin" />
      </main>
    );
  }

  if (user) return null;

  return (
    <main className="min-h-screen geometric-bg flex flex-col items-center justify-center px-4 py-8 pb-24">
      <div className="w-full max-w-sm flex flex-col items-center">
        {/* Logo */}
        <div className="w-20 h-20 rounded-2xl overflow-hidden mb-4 border-2 border-accent/30">
          <Image
            src="/assets/images/imaan_mate_just_logo-1778074141773.png"
            alt="Imaan Mate logo"
            width={80}
            height={80}
            className="w-full h-full object-cover"
          />
        </div>

        {/* App name & tagline */}
        <h1 className="text-3xl font-bold text-foreground mb-1">Imaan Mate</h1>
        <p className="text-base text-accent font-medium mb-3">Your AI Deen Companion</p>

        {/* Brief description */}
        <p className="text-sm text-muted-foreground text-center mb-10 leading-relaxed max-w-xs">
          Search the Quran Tafsir, Hadith, and Islamic texts. Get AI-powered answers to your questions and save passages for later reflection.
        </p>

        {/* Google Sign In for all users */}
        <button
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
          className="w-full py-3.5 rounded-xl border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted/80 transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-sm"
        >
          {googleLoading ? (
            <RefreshCw size={18} className="animate-spin" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
            </svg>
          )}
          {googleLoading ? 'Signing in…' : 'Continue with Google'}
        </button>

        {/* Apple Sign In — iOS only */}
        {isIOS && (
          <button
            onClick={handleAppleSignIn}
            disabled={appleLoading}
            className="mt-3 w-full py-3.5 rounded-xl border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted/80 transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-sm"
          >
            {appleLoading ? (
              <RefreshCw size={18} className="animate-spin" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 814 1000" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.6-49.1 190.5-49.1zm-134.5-116.6c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
              </svg>
            )}
            {appleLoading ? 'Signing in…' : 'Continue with Apple'}
          </button>
        )}

        {error && (
          <p className="mt-4 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 w-full text-center">
            {error}
          </p>
        )}
      </div>

      <BottomNav />
    </main>
  );
}
